import React, { useEffect } from 'react'
import { IntercomAPI } from 'react-intercom'
import GenericConnectionModalContent from '../channels/generic/GenericConnectionModalContent'
import ShipstationConnectionModalContent from '../channels/shipstation/ShipstationConnectionModalContent'
import ShippoConnectionModalContent from '../channels/shippo/ShippoConnectionModalContent'
import SlackSettingsModalContent from '../channels/slack/SlackSettingsModalContent'
import APIStoreConnectionModalContent from '../channels/apistore/ApiStoreConnectionModal'
import {
  openConnectionModal,
  openCsvModal,
  openInkfrogCsvModal,
  openSettingsModal,
  postSlackMessage,
  setCurrentChannel,
  setCurrentChannelAccount,
} from '../integrationsSlice'
import { linkFromChannel } from '../../../modules/account'
import { SLACK_DATA_STATUS_CHANNEL_ID } from '../../../modules/constants'
import { getCookie } from '../../../modules/cookie'
import { useDispatch, useSelector } from 'react-redux'
import { planIntegrationLimits, storeChannels } from '../../../applications/Plan/planConfig'
import { selectRootAdmin } from '../../../rootSlice'
import ShipsuranceConnectionModalContent from '../channels/shipsurance/ShipsuranceConnectionModalContent'
import OpenAIConnectionModalContent from '../channels/openai/OpenAIConnectionModalContent'
import PayHelmMcpConnectionModalContent from '../channels/mcp/PayHelmMcpConnectionModalContent'
import GoogleConnectionModal from '../channels/google/GoogleConnectionModal'
import PayHelmPixelModalContent from '../channels/payhelmPixel/PayHelmPixelModalContent'
import FeedModalContent from '../channels/feed/FeedModalContent'
import GoogleMerchantCenterConnectionModalContent from '../channels/googleMerchantCenter/GoogleMerchantCenterConnectionModalContent'
import { ChannelAdvisorConnectionModal } from '../channels/channeladvisor'
import StripeConnectionModalContent from '../channels/stripe/StripeConnectionModalContent'
import MakeswiftConnectionModalContent from '../channels/makeswift/MakeswiftConnectionModalContent'
import FeedonomicsConnectionModalContent from '../channels/feedonomics/FeedonomicsConnectionModalContent'
import OpenAIAdsConnectionModalContent from '../channels/openaiAds/OpenAIAdsConnectionModalContent'
import BigQueryConnectionModalContent from '../channels/bigquery/BigQueryConnectionModalContent'
import WordPressConnectionModalContent from '../channels/wordpress/WordPressConnectionModalContent'
import { topRedirect, topRedirectToSlack } from 'utils'
import { baseUrl } from 'urls'

export function useConnectionModalContent({ ownerId, sharedAccountIds, channel, retrieveAccounts, accounts, allPublicKeys }) {
  const { channel: channelName } = channel || {}
  const channelToModalContentMap = {
    OpenAI: <OpenAIConnectionModalContent ownerId={ownerId} channel={channel} retrieveAccounts={retrieveAccounts} allPublicKeys={allPublicKeys} />,
    Shipstation: <ShipstationConnectionModalContent retrieveAccounts={retrieveAccounts} />,
    Shippo: (
      <ShippoConnectionModalContent
        ownerId={ownerId}
        channel={channel}
        retrieveAccounts={retrieveAccounts}
        accounts={accounts}
        allPublicKeys={allPublicKeys}
      />
    ),
    'PayHelm MCP AI': <PayHelmMcpConnectionModalContent ownerId={ownerId} sharedAccountIds={sharedAccountIds} channel={channel} />,
    Shipsurance: <ShipsuranceConnectionModalContent retrieveAccounts={retrieveAccounts} />,
    'API Store': <APIStoreConnectionModalContent retrieveAccounts={retrieveAccounts} />,
    'Google Eco': <GoogleConnectionModal />,
    'PayHelm Pixel': <PayHelmPixelModalContent newConnection={true} accounts={accounts} retrieveAccounts={retrieveAccounts}/>,
    'Authorize.Net': <GenericConnectionModalContent ownerId={ownerId} channel={channel} retrieveAccounts={retrieveAccounts} accountLabel={'Account Name'} publicKeyLabel={'API Login ID'} privateKeyLabel={'Transaction Key'} />,
    'Feedonomics': (
      <FeedonomicsConnectionModalContent
        ownerId={ownerId}
        channel={channel}
        retrieveAccounts={retrieveAccounts}
        allPublicKeys={allPublicKeys}
      />
    ),
    'OpenAI Ads': (
      <OpenAIAdsConnectionModalContent
        ownerId={ownerId}
        channel={channel}
        retrieveAccounts={retrieveAccounts}
        allPublicKeys={allPublicKeys}
      />
    ),
    'Google BigQuery': (
      <BigQueryConnectionModalContent ownerId={ownerId} retrieveAccounts={retrieveAccounts} />
    ),
    'DataFeedWatch': <FeedModalContent retrieveAccounts={retrieveAccounts} channel={channel} />,
    'Google Merchant Center': (
      <GoogleMerchantCenterConnectionModalContent
        ownerId={ownerId}
        channel={channel}
        retrieveAccounts={retrieveAccounts}
        allPublicKeys={allPublicKeys}
      />
    ),
    'ChannelAdvisor': <ChannelAdvisorConnectionModal />,
    'Stripe': <StripeConnectionModalContent ownerId={ownerId} channel={channel} retrieveAccounts={retrieveAccounts} />,
    Makeswift: (
      <MakeswiftConnectionModalContent
        ownerId={ownerId}
        channel={channel}
        retrieveAccounts={retrieveAccounts}
        allPublicKeys={allPublicKeys}
      />
    ),
    WordPress: <WordPressConnectionModalContent />,
  }

  // Fall back to generic modal content if not specified
  const modalContent = channelToModalContentMap[channelName] || (
    <GenericConnectionModalContent ownerId={ownerId} channel={channel} retrieveAccounts={retrieveAccounts} />
  )

  if (!channel) return { content: null }

  return { content: modalContent }
}

export function useSettingsModalContent({ account, accounts, retrieveAccounts, channel }) {
  const { channel: channelName } = account || {}
  const channelToModalContentMap = {
    Slack: <SlackSettingsModalContent />,
    'PayHelm Pixel': <PayHelmPixelModalContent accounts={accounts} retrieveAccounts={retrieveAccounts}/>,
    'Feedonomics': <FeedModalContent retrieveAccounts={retrieveAccounts} channel={channel} mode="settings"/>,
    'DataFeedWatch': <FeedModalContent retrieveAccounts={retrieveAccounts} channel={channel} mode="settings"/>,
  }

  const modalContent = channelToModalContentMap[channelName] || null

  if (!account || !channelName) return { content: null }

  return { content: modalContent }
}

/**
 * Handle connection click
 *
 * @remarks
 * This function is used to handle the connection click for the integrations.
 * Open connection modal for integrations that are not yet implemented.
 * Open CSV modal for upload integration.
 * Link from channel for special cases.
 * Send message to slack channel for notification.
 * Track integration connect events for analytics via Intercom.
 *
 * @typedef {Object} Integration - Integration object
 * @property {string} id - Integration ID
 * @property {string} channel - Integration channel
 * @property {boolean} enabled - Integration enabled
 * @property {string} title - Integration title
 *
 * @param {Object} user - User object
 * @param {Object} integration - Integration object
 * @returns {Function} onConnectClick function
 *
 */
export function useConnectionClick({ user, integration, onLimitExceeded }) {
  const dispatch = useDispatch()
  const subscription = useSelector((state: any) => state.plan.subscription)
  const integrationCounts = useSelector((state: any) => state.plan.integrationCounts)
  const isAdmin = useSelector(selectRootAdmin)
  const isTrialing = subscription?.status === 'trialing'

  /**
   * Handle special cases
   */
  const onConnectClick = () => {
    const { id: channelId, enabled: channelEnabled, channel, connected } = integration
    const { email } = user

    // Check integration limits before connecting (skip if already connected, trialing, or admin)
    if (!connected && subscription && integrationCounts && !isAdmin && !isTrialing) {
      const currentPlanProduct = (subscription.plans || [])[0]?.product
      const limits = planIntegrationLimits[currentPlanProduct]
      if (limits) {
        const isStore = storeChannels.includes(channel)
        if (isStore && limits.stores !== null && integrationCounts.stores >= limits.stores) {
          if (onLimitExceeded) onLimitExceeded('store')
          return
        }
        if (!isStore && limits.integrations !== null && integrationCounts.integrations >= limits.integrations) {
          if (onLimitExceeded) onLimitExceeded('integration')
          return
        }
      }
    }

    dispatch(setCurrentChannel(integration))

    if (!channelEnabled) {
      // For integrations that are not yet implemented, we show it a generic "secret key" input to start a conversation
      dispatch(openConnectionModal())

      // Send message to a slack channel for notification
      dispatch(
        postSlackMessage({
          channel: SLACK_DATA_STATUS_CHANNEL_ID,
          text: `${email} clicked connect button for ${integration.title} integration`,
        })
      )

      // Track it via intercom
      IntercomAPI('trackEvent', 'clicked-integration-connect-button', {
        email,
        channel,
      })

      return
    }

    if ([
      'shipstation', 'shippo', 'openai', 'openai_ads', 'payhelm_ai_mcp', 'shipsurance',
      'api_store', 'google', 'payhelm_pixel',
      'feedonomics', 'datafeedwatch',
      'channeladvisor', 'stripe', 'makeswift', 'wordpress',
    ].includes(channelId)) {
      dispatch(openConnectionModal())
      return
    }

    if (channelId === 'google_merchant_center') {
      dispatch(openConnectionModal())
      return
    }

    if (channelId === 'google_search_console') {
      linkFromChannel('google_search_console')
      return
    }

    if (channelId === 'bigquery') {
      dispatch(openConnectionModal())
      return
    }

    if (channelId === 'google_marketing') {
      linkFromChannel('google_marketing')
      return
    }

    if (channelId === 'google_analytics') {
      linkFromChannel('google_analytics')
      return
    }

    if (channelId === 'google_tag_manager') {
      linkFromChannel('google_tag_manager')
      return
    }

    if (channelId === 'google_ads') {
      linkFromChannel('google ads')
      return
    }

    if (channelId === 'google_drive') {
      linkFromChannel('google_drive')
      return
    }

    if (channelId === 'slack') {
      topRedirectToSlack()
      return
    }

    if (channelId === 'teams') {
      // `state` carries the user so /auth/teams/accept can bind the install to them.
      const unique_user_id = getCookie('unique_user_id')
      topRedirect({
        url: `${baseUrl}/auth/teams/install?state=${encodeURIComponent(unique_user_id ?? '')}`
      })
      return
    }

    if (channelId === 'upload') {
      dispatch(openCsvModal())
      return
    }

    if (channelId === 'inkfrog_listings') {
      dispatch(openInkfrogCsvModal(undefined))
      return
    }

    // Track integration connect events for analytics
    IntercomAPI('trackEvent', 'clicked-integration-connect-button', {
      email,
      channel,
    })

    linkFromChannel(channelId)
  }

  return onConnectClick
}
